import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, filter, first, Subject, takeUntil, forkJoin } from 'rxjs';
import { faArrowUpLong as farArrowUpLong, faArrowDownLong as farArrowDownLong } from '@fortawesome/pro-regular-svg-icons';
import { MatDialog } from '@angular/material/dialog';
import { LgvAverageRatesService } from '../../core/services/lgv-average-rates.service';
import { LgvTodaysRatesServiceService } from '../../core/services/lgv-todays-rates-service.service';
import { LgvAuth0Service, LgvClientService, LgvUserService, NotificationService } from '../../core/core.module';
import { InfoPopup } from '../info-popup/info-popup.component';
import { LgvModel } from '../../data-models/longevity.model';
import { LgvAuthMgrService } from '../../core/services/lgv-auth-mgr.service';
import { InfoDetail, InfoHeader } from '../../modules/default/pages/avgCompare/info-message.enum';
import { LocationService } from '../../core/services/location.service';

@Component({
    selector: 'lgv-todays-rates',
    templateUrl: './todays-rates.component.html',
    styleUrls: ['./todays-rates.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TodaysRatesComponent implements OnInit, OnDestroy {

    constructor(private route: ActivatedRoute,
        public lgvAvgRatesSvc: LgvAverageRatesService,
        public lgvTodaysRatesSvc: LgvTodaysRatesServiceService,
        public lgvUserSvc: LgvUserService,
        private notificationSvc: NotificationService,
        private dialog: MatDialog,
        private router: Router,
        private authMgrSvc: LgvAuthMgrService,
        private locationService: LocationService
    ) { }

    @ViewChild('outerContainer') outerContainerRef: ElementRef;

    agentDTO$ = new BehaviorSubject(null as LgvModel.AgentDTO);

    onDestroy$ = new Subject();
    farArrowUpLong = farArrowUpLong;
    farArrowDownLong = farArrowDownLong;

    avgRates$ = new BehaviorSubject<any[]>([]);
    rateDate = new Date();

    activeIndex = 0;
    isLoading$ = new BehaviorSubject<boolean>(true);
    productsWithAvgRates$ = new BehaviorSubject<any>({});

    locationDetails: any;

    ngOnInit(): void {
        this.lgvUserSvc.AgentDTO$.pipe(filter(f => !!f), first()).subscribe(agentDTO => {
            this.agentDTO$.next(agentDTO);
            
            // Initialize the service to fetch average rates
            this.lgvTodaysRatesSvc.init();
            
            // Define the loan types we want to fetch - matching AvgCompare
            // Get rate and average pairs for the same products as AvgCompare
            forkJoin([
                this.lgvTodaysRatesSvc.GetProductRateAndAverage(
                    agentDTO.agentInfo.id, 
                    undefined, // state
                    undefined, // zipCode 
                    undefined, // county
                    'Conventional',
                    30,
                    undefined, // purchasePrice
                    undefined // downPaymentPercent
                ),
                this.lgvTodaysRatesSvc.GetProductRateAndAverage(
                    agentDTO.agentInfo.id, 
                    undefined, // state
                    undefined, // zipCode 
                    undefined, // county
                    'Conventional',
                    15,
                    undefined, // purchasePrice
                    undefined // downPaymentPercent
                ),
                this.lgvTodaysRatesSvc.GetProductRateAndAverage(
                    agentDTO.agentInfo.id, 
                    undefined, // state
                    undefined, // zipCode 
                    undefined, // county
                    'FHA',
                    30,
                    undefined, // purchasePrice
                    undefined // downPaymentPercent
                ),
                this.lgvTodaysRatesSvc.GetProductRateAndAverage(
                    agentDTO.agentInfo.id, 
                    undefined, // state
                    undefined, // zipCode 
                    undefined, // county
                    'NonConforming',
                    30,
                    undefined, // purchasePrice
                    undefined // downPaymentPercent
                )
            ])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: (results) => {
                    // Transform results to expected format
                    const products = {};
                    results.forEach(result => {
                        // Make sure todaysRate includes model data needed for info popups
                        if (result.todaysRate) {
                            // Add the model data with required structure if not already present
                            if (!result.todaysRate.model) {
                                result.todaysRate.model = {
                                    loanInformation: {
                                        baseLoanAmount: 0
                                    },
                                    propertyInformation: {
                                        appraisedValue: 0
                                    }
                                };
                            }
                        }
                        
                        products[result.product] = {
                            averageRate: result.averageRate,
                            todaysRate: result.todaysRate
                        };
                    });
                    
                    this.productsWithAvgRates$.next(products);
                    this.isLoading$.next(false);
                },
                error: (err) => {
                    console.error('Error fetching product rates:', err);
                    this.isLoading$.next(false);
                    this.notificationSvc.error('Error fetching rates information.');
                }
            });
        });
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    showInfoModal(infoType: string, data: { [key: string]: any } = {}) {
        let infoBody = InfoDetail[infoType];

        if (!data) {
            data = {};
        }

        // Replace each placeholder, use fallback value if undefined
        Object.keys(data).forEach(key => {
            const value = data[key] ?? 'N/A';
            const placeholder = `{{${key}}}`;
            infoBody = infoBody.replace(new RegExp(placeholder, 'g'), value);
        });

        const dialogRef = this.dialog.open(InfoPopup, {
            width: '500px',
            data: {
                header: InfoHeader[infoType],
                body: infoBody,
                mode: 'generic'
            }
        });
    }

    // Slide change events
    onSlideChangeStart() {
        this.activeIndex = null;
    }
    onSlideChangeEnd(event: any) {
        this.activeIndex = event.detail[0].activeIndex;
    }

    // Check if the current slide is active
    isActiveSlide(index: number): boolean {
        return this.activeIndex === index;
    }

    goToPricing() {
        window.open('/#/admin/pricing-scenario', '_blank')
    }

    roundOff(number) {
        return +(+(Math.round(+(number + 'e+' + 3)) + 'e-' + 3)).toFixed(3);
    }

    formatPoints(price: number): string {
        if (!price) return 'N/A';
        const priceType = price > 0 ? 'discount' : 'rebate';
        return `${Math.abs(100 - price).toFixed(3)}% ${priceType}`;
    }
}